import cx from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import Avatar from "react-avatar";

import logoImg from "../assets/logo.png";
import ModalCommunities from "../ModalCommunity";
import ModalUserEditor, { useModalUserEditor } from "../ModalUserEditor";
import CommunitiesIcon from "./assets/communities.svg?react";
import HomeIcon from "./assets/home.svg?react";
import ToolsIcon from "./assets/tools.svg?react";
import UsersIcon from "./assets/users.svg?react";
import { useUiGrantAccess } from "@/lib/accessControl";
import { DataProvider } from "@/lib/DataProvider";
import { useAuth } from "@/lib/useAuth";
import ModalAddressEditor from "@/ModalAddressEditor";
import ModalAddressImport from "@/ModalAddressImport";
import ModalAddressImportEditor from "@/ModalAddressImportEditor";
import ModalDelete from "@/ModalDelete";
import ModalQuestion from "@/ModalQuestion";
import ModalSignin from "@/ModalSignin";
import ModalZone from "@/ModalZone";
import PartDropDownMenu from "@/PartDropDownMenu";
import { ApiUserT } from "@/types/api-types";

import styles from "./Layout.module.scss";

type LayoutProps = React.PropsWithChildren;

const Layout: React.FC<LayoutProps> = props => {
  const { children } = props;
  const { asPath, push, pathname } = useRouter();
  const { signOut, $disfigureSession } = useAuth();
  const allowUsersDashboard = useUiGrantAccess("/users/dashboard");

  const { startEdit } = useModalUserEditor();

  const topMenu = React.useMemo(
    () =>
      [
        {
          title: "Dashboard",
          href: "/home",
          Icon: HomeIcon,
          active: pathname === "/home",
        },
        {
          title: "Communities",
          href: "/communities",
          Icon: CommunitiesIcon,
          active: pathname === "/communities",
        },
        allowUsersDashboard && {
          title: "Users",
          href: "/users",
          Icon: UsersIcon,
          active: pathname === "/users",
        },
        {
          title: "Tools",
          href: "/tools",
          Icon: ToolsIcon,
          active: pathname.startsWith("/tools"),
        },
      ].filter(it => !!it),
    [pathname, allowUsersDashboard],
  );

  const [lastUrlInTopMenuBeforeExit, setLastUrlInTopMenuBeforeExit] = React.useState<
    Record<string, string>
  >({});

  const handleClickOnTopMenu = React.useCallback(
    ({ key, href }: { key: string; href: string }) => {
      const currentActiveTopMenuKey = topMenu.find(it => it.active)?.title;
      if (currentActiveTopMenuKey) {
        setLastUrlInTopMenuBeforeExit(it => ({
          ...it,
          [currentActiveTopMenuKey]: asPath,
        }));
      }
      push(lastUrlInTopMenuBeforeExit[key] || href).then();
    },
    [topMenu, asPath, lastUrlInTopMenuBeforeExit],
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.logoContainer}>
          <img src={logoImg.src} alt="logo" />
        </div>
        <div className={styles.navLinksContainer} data-testid="navLinksContainer">
          {topMenu.map(({ title, Icon, href, active }) => (
            <Link
              key={title}
              href={href}
              className={cx(styles.navLink, { [styles.active]: active })}
              onClick={event => {
                event.preventDefault();
                handleClickOnTopMenu({ key: title, href });
              }}
            >
              <Icon />
              <span>{title}</span>
            </Link>
          ))}
        </div>
        <div className={styles.spacer}></div>
        <DataProvider url={process.env.NEXT_PUBLIC_API_URL + "/api/v1/user/me"}>
          {(user: ApiUserT | undefined) => (
            <PartDropDownMenu
              menu={[
                {
                  title: "Edit Profile",
                  onClick: () => startEdit(String(user?.id)),
                  extraAttributes: {
                    "data-test-element-type": "button",
                    "data-test-action-type": "start-edit-user-by-self",
                  },
                },
                {
                  title: "Log Out",
                  onClick: () => signOut(),
                  extraAttributes: {
                    "data-test-element-type": "button",
                    "data-test-action-type": "start-logout-user-by-self",
                  },
                },
                (process.env.NODE_ENV === "development" ||
                  (globalThis.window &&
                    globalThis.window.location.host ===
                      "dev.hhdb.dev.circularmaterials.cloud")) && {
                  title: "DEV_ONLY disfigure session",
                  onClick: () => $disfigureSession(),
                  extraAttributes: {
                    "data-test-element-type": "button",
                    "data-test-action-type": "disfigure-session",
                  },
                },
              ].filter(it => !!it)}
            >
              <div data-testid="avatar">
                <Avatar
                  round
                  name={user?.name || undefined}
                  size="32"
                  className={styles.avatar}
                  textSizeRatio={2}
                />
              </div>
            </PartDropDownMenu>
          )}
        </DataProvider>
      </div>
      <div className={styles.main}>{children}</div>
      <ModalQuestion />
      <ModalAddressEditor />
      <ModalAddressImport />
      <ModalAddressImportEditor />
      <ModalCommunities />
      <ModalZone />
      <ModalDelete />
      <ModalUserEditor />
      <ModalSignin />
    </div>
  );
};

export default Layout;
